import { ExternalLink } from 'lucide-react'
import { useState } from 'react'
import IndexToolbar from '@/components/IndexToolbar'
import { matchesQuery } from '@/lib/matchesQuery'
import { useFormSubmissions } from '@/features/forms/useForms'
import { formatAnswer, submittedAt, submitterLabel } from '@/features/forms/formBuilder'
import type { Form } from '@/api/forms'

/**
 * What a form actually captured, as a table rather than one run-on line of raw field keys: when it
 * arrived, who it created, and each answer under its own form label. The contact and deal open in new
 * tabs so working through a batch of leads doesn't lose the form list.
 */
export default function FormSubmissionsTable({ form }: { form: Form }) {
  const { data } = useFormSubmissions(form.id)
  const [query, setQuery] = useState('')
  const all = data ?? []
  const subs = all.filter((s) => matchesQuery(query, ...Object.values(s.data).map((v) => formatAnswer(v))))
  if (all.length === 0) return <p className="text-xs text-gray-400 px-4 py-2">No submissions yet.</p>

  // Column per form field, in the builder's own order — answers to fields since removed still show,
  // appended, rather than vanishing from the record of what the lead actually sent.
  const labels = new Map(form.fields.map((f) => [f.key, f.label || f.key]))
  const extraKeys = [...new Set(subs.flatMap((s) => Object.keys(s.data)))].filter((k) => !labels.has(k))
  const columns = [...form.fields.map((f) => f.key), ...extraKeys]

  return (
    <div className="px-4 pb-3">
      {all.length > 5 && (
        <div className="pt-3">
          <IndexToolbar query={query} onQueryChange={setQuery} placeholder="Search submissions by name, email, phone or answer…" shown={subs.length} total={all.length} />
        </div>
      )}
      <div className="overflow-x-auto border-t border-gray-100">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-gray-400">
              <th className="py-1.5 pr-3 font-medium whitespace-nowrap">Submitted</th>
              <th className="py-1.5 pr-3 font-medium whitespace-nowrap">Created</th>
              {columns.map((k) => (
                <th key={k} className="py-1.5 pr-3 font-medium whitespace-nowrap">{labels.get(k) ?? k}</th>
              ))}
            </tr>
          </thead>
          <tbody className="text-gray-600">
            {subs.map((s) => (
              <tr key={s.id} className="border-t border-gray-100 align-top">
                <td className="py-1.5 pr-3 whitespace-nowrap text-gray-500">{submittedAt(s.createdAt)}</td>
                <td className="py-1.5 pr-3 whitespace-nowrap">
                  <div className="flex flex-col gap-0.5">
                    {s.contactId ? (
                      <a
                        href={`/contacts/${s.contactId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-primary hover:underline inline-flex items-center gap-1"
                      >
                        {submitterLabel(s.data)} <ExternalLink size={9} />
                      </a>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                    {s.dealId && (
                      <a
                        href={`/deals/${s.dealId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-success hover:underline inline-flex items-center gap-1"
                      >
                        deal <ExternalLink size={9} />
                      </a>
                    )}
                  </div>
                </td>
                {columns.map((k) => (
                  <td key={k} className="py-1.5 pr-3 max-w-[16rem] break-words">
                    {formatAnswer(s.data[k])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

